import React from "react";
import { connect } from "react-redux";
import Loader from "../../../components/Loader/Loader";
import {
  fetchSettings,
  saveSettings,
  syncEnvFile,
} from "../../../actions/systemSettingsActions";

const SETTING_GROUPS = [
  {
    key: "auth",
    title: "Auth & Security",
    description:
      "Platform-wide authentication controls. When email verification is enabled, new users must confirm their email before they can log in. Existing accounts are grandfathered when you turn this on.",
    syncEnv: false,
    fields: [
      {
        key: "EMAIL_VERIFICATION_ENABLED",
        label: "Require email verification for new users",
        type: "toggle",
        help: "When ON, signup sends a confirmation link and login is blocked until the email is verified.",
      },
    ],
  },
  {
    key: "stripe",
    title: "Stripe Configuration",
    description: "Manage your Stripe API keys and webhook configuration. These are used for platform subscription billing.",
    fields: [
      { key: "STRIPE_PUBLISHABLE_KEY", label: "Publishable Key", type: "text", placeholder: "pk_live_..." },
      { key: "STRIPE_SECRET_KEY", label: "Secret Key", type: "password", placeholder: "sk_live_..." },
      { key: "STRIPE_CLIENT_ID", label: "Client ID (Connect)", type: "text", placeholder: "ca_..." },
      { key: "STRIPE_WEBHOOK_SECRET", label: "Webhook Secret (Subscriptions)", type: "password", placeholder: "whsec_..." },
      { key: "STRIPE_WEBHOOK_SECRET_SAVE_INVOICE", label: "Webhook Secret (Save Invoice / Paid Bookings)", type: "password", placeholder: "whsec_..." },
    ],
  },
  {
    key: "twilio",
    title: "Twilio Configuration",
    description: "Manage your Twilio credentials for SMS notifications and voice mail.",
    fields: [
      { key: "TWILIO_SID", label: "Account SID", type: "password", placeholder: "AC..." },
      { key: "TWILIO_AUTH_TOKEN", label: "Auth Token", type: "password", placeholder: "Your Twilio auth token" },
      { key: "TWILIO_NUMBER", label: "Phone Number", type: "text", placeholder: "+1234567890" },
    ],
  },
  {
    key: "paypal",
    title: "PayPal Configuration",
    description: "Manage your PayPal integration settings.",
    fields: [
      { key: "PAYPAL_CLIENT_ID", label: "Client ID", type: "text", placeholder: "Your PayPal client ID" },
      { key: "PAYPAL_SECRET_KEY", label: "Secret Key", type: "password", placeholder: "Your PayPal secret" },
      { key: "PAYPAL_MODE", label: "Mode", type: "text", placeholder: "sandbox or live" },
      { key: "PAYPAL_CURRENCY", label: "Currency", type: "text", placeholder: "USD" },
    ],
  },
  {
    key: "zoom",
    title: "Zoom Configuration",
    description: "Manage your Zoom OAuth credentials.",
    fields: [
      { key: "ZOOM_CLIENT_ID", label: "Client ID", type: "text", placeholder: "Your Zoom client ID" },
      { key: "ZOOM_CLIENT_SECRET", label: "Client Secret", type: "password", placeholder: "Your Zoom client secret" },
    ],
  },
];

const Toggle = ({ checked, onChange, label }) => (
  <label
    onClick={() => onChange(!checked)}
    style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", userSelect: "none" }}
  >
    <div
      style={{
        width: 44,
        height: 24,
        borderRadius: 12,
        background: checked ? "#10b981" : "#d1d5db",
        position: "relative",
        transition: "background 0.2s",
        flexShrink: 0,
      }}
    >
      <div
        style={{
          width: 18,
          height: 18,
          borderRadius: 9,
          background: "#fff",
          position: "absolute",
          top: 3,
          left: checked ? 23 : 3,
          transition: "left 0.2s",
          boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
        }}
      />
    </div>
    <span style={{ fontSize: 14, color: "#374151" }}>{label}</span>
  </label>
);

class SystemSettings extends React.Component {
  state = {
    formData: {},
    activeGroup: "auth",
    successMsg: "",
    showPasswords: {},
    syncSuccessMsg: "",
  };

  componentDidMount() {
    this.props.fetchSettings();
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.fetchSuccess && this.props.fetchSuccess) {
      const formData = {};
      SETTING_GROUPS.forEach((group) => {
        const groupSettings = this.props.settings?.[group.key] || {};
        group.fields.forEach((field) => {
          if (field.type === "toggle") {
            const raw = groupSettings[field.key];
            formData[field.key] = raw === "1" || raw === 1 || raw === true || raw === "true";
          } else {
            formData[field.key] = groupSettings[field.key] || "";
          }
        });
      });
      this.setState({ formData });
    }
  }

  handleChange = (key, value) => {
    this.setState((prev) => ({
      formData: { ...prev.formData, [key]: value },
    }));
  };

  togglePassword = (key) => {
    this.setState((prev) => ({
      showPasswords: { ...prev.showPasswords, [key]: !prev.showPasswords[key] },
    }));
  };

  saveGroup = (groupKey) => {
    const group = SETTING_GROUPS.find((g) => g.key === groupKey);
    const settings = {};
    group.fields.forEach((field) => {
      if (field.type === "toggle") {
        settings[field.key] = this.state.formData[field.key] ? "1" : "0";
      } else {
        settings[field.key] = this.state.formData[field.key] || "";
      }
    });

    this.props.saveSettings(groupKey, settings, () => {
      this.setState({ successMsg: `${group.title} saved successfully!` });
      setTimeout(() => this.setState({ successMsg: "" }), 3000);
    });
  };

  syncGroup = (groupKey) => {
    const group = SETTING_GROUPS.find((g) => g.key === groupKey);
    this.props.syncEnvFile(groupKey, () => {
      this.setState({ syncSuccessMsg: `${group.title} synced to .env file!` });
      setTimeout(() => this.setState({ syncSuccessMsg: "" }), 3000);
    });
  };

  render() {
    const { activeGroup, formData, successMsg, syncSuccessMsg } = this.state;

    return (
      <div className="eventdetailsaddbox rd_noshadow">
        <Loader isShowLoader={this.props.loading} />
        <div className="rd_supertoptwocom">
          <div className="boxheader rd_floatingheaderthig">
            <div className="rd_inputselectheader">
              <div className="rd_selectheaderrdt2 rd_selectheaderrdt2profile">
                <h3><strong>System Settings</strong></h3>
              </div>
            </div>
          </div>
        </div>

        {(successMsg || syncSuccessMsg) && (
          <div style={{ background: "#d4edda", color: "#155724", padding: "10px 16px", borderRadius: 4, marginBottom: 12, fontWeight: 500 }}>
            {successMsg || syncSuccessMsg}
          </div>
        )}

        <div style={{ display: "flex", gap: 0, marginBottom: 20, borderBottom: "2px solid #eee" }}>
          {SETTING_GROUPS.map((group) => (
            <button
              key={group.key}
              onClick={() => this.setState({ activeGroup: group.key })}
              style={{
                padding: "12px 24px",
                border: "none",
                borderBottom: activeGroup === group.key ? "3px solid #f0932b" : "3px solid transparent",
                background: "transparent",
                cursor: "pointer",
                fontWeight: activeGroup === group.key ? 700 : 400,
                color: activeGroup === group.key ? "#f0932b" : "#666",
                fontSize: 14,
                transition: "all 0.2s",
              }}
            >
              {group.title}
            </button>
          ))}
        </div>

        {SETTING_GROUPS.filter((g) => g.key === activeGroup).map((group) => (
          <div key={group.key} style={{ background: "#fff", border: "1px solid #eee", borderRadius: 8, padding: 24 }}>
            <div style={{ marginBottom: 20 }}>
              <h4 style={{ margin: 0, color: "#333", marginBottom: 4 }}>{group.title}</h4>
              <p style={{ margin: 0, color: "#888", fontSize: 13 }}>{group.description}</p>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: group.fields.some((f) => f.type === "toggle") ? "1fr" : "1fr 1fr", gap: 16 }}>
              {group.fields.map((field) => (
                <div key={field.key}>
                  {field.type === "toggle" ? (
                    <>
                      <Toggle
                        checked={!!formData[field.key]}
                        onChange={(val) => this.handleChange(field.key, val)}
                        label={field.label}
                      />
                      {field.help && (
                        <p style={{ margin: "8px 0 0", fontSize: 12, color: "#888" }}>{field.help}</p>
                      )}
                      <span style={{ fontSize: 11, color: "#aaa" }}>{field.key}</span>
                    </>
                  ) : (
                    <>
                      <label style={{ fontSize: 12, fontWeight: 600, color: "#555", marginBottom: 4, display: "block" }}>
                        {field.label}
                      </label>
                      <div style={{ position: "relative" }}>
                        <input
                          type={field.type === "password" && !this.state.showPasswords[field.key] ? "password" : "text"}
                          value={formData[field.key] || ""}
                          onChange={(e) => this.handleChange(field.key, e.target.value)}
                          placeholder={field.placeholder}
                          style={{
                            width: "100%",
                            padding: "10px 12px",
                            paddingRight: field.type === "password" ? 40 : 12,
                            border: "1px solid #ddd",
                            borderRadius: 4,
                            fontSize: 13,
                            boxSizing: "border-box",
                          }}
                        />
                        {field.type === "password" && (
                          <button
                            type="button"
                            onClick={() => this.togglePassword(field.key)}
                            style={{
                              position: "absolute",
                              right: 8,
                              top: "50%",
                              transform: "translateY(-50%)",
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              color: "#888",
                              fontSize: 12,
                            }}
                          >
                            {this.state.showPasswords[field.key] ? "Hide" : "Show"}
                          </button>
                        )}
                      </div>
                      <span style={{ fontSize: 11, color: "#aaa" }}>{field.key}</span>
                    </>
                  )}
                </div>
              ))}
            </div>

            <div style={{ marginTop: 24, display: "flex", gap: 12, alignItems: "center" }}>
              <button
                onClick={() => this.saveGroup(group.key)}
                disabled={this.props.saving}
                style={{
                  background: "#f0932b",
                  color: "#fff",
                  border: "none",
                  padding: "10px 28px",
                  borderRadius: 4,
                  cursor: "pointer",
                  fontWeight: 600,
                  fontSize: 14,
                }}
              >
                {this.props.saving ? "Saving..." : "Save to Database"}
              </button>
              {group.syncEnv !== false && (
                <>
                  <button
                    onClick={() => this.syncGroup(group.key)}
                    disabled={this.props.syncing}
                    style={{
                      background: "#27ae60",
                      color: "#fff",
                      border: "none",
                      padding: "10px 28px",
                      borderRadius: 4,
                      cursor: "pointer",
                      fontWeight: 600,
                      fontSize: 14,
                    }}
                  >
                    {this.props.syncing ? "Syncing..." : "Sync to .env File"}
                  </button>
                  <span style={{ fontSize: 12, color: "#999" }}>
                    Save stores values in database. Sync writes them to the server .env file.
                  </span>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  loading: state.systemSettings.loading,
  saving: state.systemSettings.saving,
  syncing: state.systemSettings.syncing,
  settings: state.systemSettings.settings,
  fetchSuccess: state.systemSettings.fetchSuccess,
});

const actionMethods = {
  fetchSettings,
  saveSettings,
  syncEnvFile,
};

export default connect(mapStateToProps, actionMethods)(SystemSettings);
